import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import { categories, articles } from '@/mocks/data';
import { generateBreadcrumbSchema, generateWebPageSchema } from '@/components/feature/SchemaOrg';
import { siteInfo } from '@/mocks/data';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const categoryArticles = articles.filter((a) => a.category === slug);

  if (!category) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-24 pb-16 text-center">
          <h1 className="text-2xl font-bold text-foreground-950">分类未找到</h1>
          <Link to="/" className="text-primary-500 hover:text-primary-600 mt-4 inline-block">返回首页</Link>
        </div>
        <Footer />
      </div>
    );
  }

  const pageUrl = `${siteInfo.url}/category/${category.slug}`;
  const breadcrumbItems = [
    { name: '首页', url: siteInfo.url },
    { name: category.name, url: pageUrl },
  ];
  const breadcrumbSchema = generateBreadcrumbSchema(breadcrumbItems);
  const webPageSchema = generateWebPageSchema(category.name, category.description, pageUrl);

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(webPageSchema) }} />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main>
          {/* Category Hero */}
          <section className="relative w-full min-h-[420px] md:min-h-[500px] flex items-center">
            <div className="absolute inset-0">
              <img
                src="https://readdy.ai/api/search-image?query=A%20dramatic%20dark%20editorial%20background%20with%20warm%20amber%20and%20deep%20brown%20gradient%2C%20mysterious%20atmosphere%2C%20magazine%20editorial%20style%2C%20luxurious%20texture%2C%20abstract%20with%20soft%20highlights%2C%20cinematic%20mood&width=1600&height=900&seq=cat-hero-2026&orientation=landscape"
                alt={category.name}
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/75"></div>
            </div>
            <div className="relative z-10 w-full px-4 md:px-6 pt-24 pb-12">
              <div className="mb-6">
                <Breadcrumb
                  items={[
                    { label: '首页', href: '/' },
                    { label: category.name },
                  ]}
                />
              </div>
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 md:w-14 md:h-14 flex items-center justify-center rounded-full bg-white/10">
                  <i className={`${category.icon} text-2xl text-white`}></i>
                </div>
                <div>
                  <h1 className="text-3xl md:text-4xl lg:text-5xl font-black font-heading text-white">
                    {category.name}
                  </h1>
                </div>
              </div>
              <p className="text-white/70 text-sm md:text-base max-w-2xl">
                {category.description} · 共 {category.count} 篇内容
              </p>
            </div>
          </section>

          {/* Articles Grid */}
          <section className="w-full py-12 md:py-16 px-4 md:px-6 bg-background-50">
            {categoryArticles.length === 0 ? (
              <div className="text-center py-20">
                <p className="text-foreground-500">该分类暂无文章</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {categoryArticles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.slug}`}
                    className="group block rounded-2xl bg-background-100 hover:bg-background-200/70 transition-all duration-300 overflow-hidden cursor-pointer"
                  >
                    <div className="relative h-48 md:h-56 overflow-hidden">
                      <img
                        src={article.imageUrl}
                        alt={article.title}
                        title={article.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                      {article.isHot && (
                        <div className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-accent-500 text-white text-xs font-bold">
                          热门
                        </div>
                      )}
                    </div>
                    <div className="p-5">
                      <h3 className="text-base font-bold font-heading text-foreground-950 group-hover:text-primary-500 transition-colors mb-2 line-clamp-2">
                        {article.title}
                      </h3>
                      <p className="text-xs text-foreground-500 mb-3 line-clamp-2">{article.excerpt}</p>
                      <div className="flex items-center gap-3 text-xs text-foreground-400">
                        <span>{article.date}</span>
                        <span>{article.views >= 10000 ? `${Math.floor(article.views / 10000)}万浏览` : `${article.views}浏览`}</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
}